import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FiTrendingUp,
  FiBarChart2,
  FiBookOpen,
  FiZap,
  FiRefreshCw,
  FiArrowLeft,
  FiActivity,
} from 'react-icons/fi';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Badge from '../../components/common/Badge';
import AIInsights from '../../components/common/AIInsights';
import { useNotification } from '../../contexts/NotificationContext';
import { testAPI } from '../../services/api';
import './ComprehensiveInsights.css';

const ComprehensiveInsights = () => {
  const [results, setResults] = useState([]);
  const [comprehensiveInsights, setComprehensiveInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingInsights, setGeneratingInsights] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});
  const [subjectBreakdown, setSubjectBreakdown] = useState([]);
  const [performanceTrends, setPerformanceTrends] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  
  // Pure helpers for calculations
  const calculateStats = useCallback((resultsData) => {
    if (resultsData.length === 0) {
      setStats({ totalTests: 0, averageScore: 0, passedTests: 0, passRate: 0, totalTime: 0 });
      return;
    }

    const totalTests = resultsData.length;
    const totalScore = resultsData.reduce((sum, result) => sum + (result.score?.percentage || 0), 0);
    const averageScore = Math.round(totalScore / totalTests);
    const passedTests = resultsData.filter(result => (result.score?.percentage || 0) >= 50).length;
    const passRate = Math.round((passedTests / totalTests) * 100);
    const totalTime = resultsData.reduce((sum, result) => sum + (result.session?.timeTaken || 0), 0);

    setStats({ totalTests, averageScore, passedTests, passRate, totalTime });
  }, []);

  const calculateSubjectBreakdown = useCallback((resultsData) => {
    const subjectMap = {};
    
    resultsData.forEach(result => {
      const subject = result.test?.subject || 'Unknown';
      if (!subjectMap[subject]) {
        subjectMap[subject] = {
          subject,
          totalTests: 0,
          totalScore: 0,
          passedTests: 0
        };
      }
      
      subjectMap[subject].totalTests++;
      subjectMap[subject].totalScore += result.score?.percentage || 0;
      if ((result.score?.percentage || 0) >= 50) {
        subjectMap[subject].passedTests++;
      }
    });

    const breakdown = Object.values(subjectMap).map(item => ({
      ...item,
      averageScore: Math.round(item.totalScore / item.totalTests),
      passRate: Math.round((item.passedTests / item.totalTests) * 100)
    }));

    setSubjectBreakdown(breakdown);
  }, []);

  const calculatePerformanceTrends = useCallback((resultsData) => {
    // Sort by date and calculate trends over time
    const sortedResults = [...resultsData].sort((a, b) => 
      new Date(a.session?.completedAt || a.createdAt) - new Date(b.session?.completedAt || b.createdAt)
    );

    const trends = [];
    const windowSize = 5; // Calculate trend over last 5 tests

    for (let i = windowSize - 1; i < sortedResults.length; i++) {
      const window = sortedResults.slice(i - windowSize + 1, i + 1);
      const averageScore = window.reduce((sum, result) => sum + (result.score?.percentage || 0), 0) / windowSize;
      
      trends.push({
        date: sortedResults[i].session?.completedAt || sortedResults[i].createdAt,
        averageScore: Math.round(averageScore),
        testCount: i + 1
      });
    }

    setPerformanceTrends(trends);
  }, []);

  const loadComprehensiveInsights = useCallback(async () => {
    try {
      // Try to get existing comprehensive insights
      const response = await testAPI.getComprehensiveInsights();
      setComprehensiveInsights(response.data?.data?.insights);
    } catch (err) {
      console.log('ℹ️ No existing comprehensive insights found');
    }
  }, []);

  const loadAllResults = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Loading all test results for comprehensive insights...');

      // Load all user test results without pagination
      const response = await testAPI.getUserTestResults({
        page: 1,
        limit: 100, // Maximum allowed by backend validation
        sortBy: 'completedAt',
        sortOrder: 'desc'
      });

      const resultsData = response.data?.data?.results || [];
      
      console.log('✅ Loaded test results for comprehensive analysis:', {
        resultsCount: resultsData.length
      });

      setResults(resultsData);
      calculateStats(resultsData);
      calculateSubjectBreakdown(resultsData);
      calculatePerformanceTrends(resultsData);

      // Check if we have existing comprehensive insights
      if (resultsData.length > 0) {
        await loadComprehensiveInsights();
      }

    } catch (err) {
      console.error('💥 Failed to load test results:', err);
      const data = err.response?.data;
      const errorMessage = data?.error || data?.message || err.message || 'Failed to load test results';
      setError(errorMessage);
      showError(`Failed to load test results: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [showError, calculateStats, calculateSubjectBreakdown, calculatePerformanceTrends, loadComprehensiveInsights]);

  useEffect(() => {
    loadAllResults();
  }, [loadAllResults]);

  const generateComprehensiveInsights = async () => {
    if (results.length === 0) {
      showError('No test results available for analysis');
      return;
    }

    try {
      setGeneratingInsights(true);
      
      console.log('🤖 Generating comprehensive AI insights...');
      
      const response = await testAPI.generateComprehensiveInsights({
        testResults: results.map(result => ({
          testId: result.test._id,
          testTitle: result.test.title,
          subject: result.test.subject,
          examType: result.test.examType,
          score: result.score,
          completedAt: result.session?.completedAt || result.createdAt,
          timeTaken: result.session?.timeTaken,
          aiInsights: result.aiInsights
        }))
      });

      const insights = response.data?.data?.insights;
      setComprehensiveInsights(insights);
      
      showSuccess('Comprehensive Expert Intelligence insights generated successfully!');
      
    } catch (err) {
      console.error('💥 Failed to generate comprehensive insights:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to generate insights';
      showError(`Failed to generate insights: ${errorMessage}`);
    } finally {
      setGeneratingInsights(false);
    }
  };


  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="ci-page">
        <div className="ci-state ci-loading">
          <LoadingSpinner size="large" />
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ci-page">
        <div className="ci-state ci-error">
          <h2>Couldn’t load data</h2>
          <p>{error}</p>
          <Button onClick={loadAllResults}>Try again</Button>
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="ci-page">
        <div className="ci-state ci-empty">
          <FiActivity size={40} />
          <h2>No results yet</h2>
          <p>Complete some tests to see insights here.</p>
          <Button onClick={() => navigate('/tests')}>Take a test</Button>
        </div>
      </div>
    );
  }

  const scoreVariant = (pct) => (pct >= 80 ? 'success' : pct >= 60 ? 'warning' : 'danger');
  const distributionBands = [
    { range: '90–100%', min: 90, max: 101, color: 'success' },
    { range: '80–89%', min: 80, max: 90, color: 'success' },
    { range: '70–79%', min: 70, max: 80, color: 'warning' },
    { range: '60–69%', min: 60, max: 70, color: 'warning' },
    { range: '< 60%', min: 0, max: 60, color: 'danger' },
  ];

  return (
    <div className="ci-page">
      <header className="ci-header">
        <Button variant="text" size="small" onClick={() => navigate('/test-results')} icon={<FiArrowLeft />} className="ci-back">
          Back to results
        </Button>
        <div className="ci-header-row">
          <div>
            <h1 className="ci-title">Comprehensive insights</h1>
            <p className="ci-subtitle">Analysis across all your test results</p>
          </div>
          <div className="ci-header-actions">
            <Button variant="outline" size="small" icon={<FiRefreshCw />} onClick={loadAllResults} disabled={loading}>
              Refresh
            </Button>
            <Button
              variant="primary"
              size="small"
              icon={<FiZap />}
              onClick={generateComprehensiveInsights}
              disabled={generatingInsights || results.length === 0}
            >
              {generatingInsights ? 'Generating…' : 'Generate insights'}
            </Button>
          </div>
        </div>
      </header>

      <div className="ci-stats">
        <div className="ci-stat"><span className="ci-stat-value">{stats.totalTests}</span><span className="ci-stat-label">Tests</span></div>
        <div className="ci-stat"><span className="ci-stat-value">{stats.averageScore}%</span><span className="ci-stat-label">Average</span></div>
        <div className="ci-stat"><span className="ci-stat-value">{stats.passRate}%</span><span className="ci-stat-label">Pass rate</span></div>
        <div className="ci-stat"><span className="ci-stat-value">{formatDuration(stats.totalTime)}</span><span className="ci-stat-label">Study time</span></div>
      </div>

      <nav className="ci-tabs">
        {[
          { id: 'overview', label: 'Overview', icon: FiBarChart2 },
          { id: 'subjects', label: 'By subject', icon: FiBookOpen },
          { id: 'trends', label: 'Trends', icon: FiTrendingUp },
          { id: 'insights', label: 'AI insights', icon: FiZap },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            className={`ci-tab ${activeTab === id ? 'active' : ''}`}
            onClick={() => setActiveTab(id)}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </nav>

      <main className="ci-content">
        {activeTab === 'overview' && (
          <div className="ci-overview">
            <section className="ci-card">
              <h2 className="ci-card-title">Recent tests</h2>
              <ul className="ci-recent-list">
                {results.slice(0, 8).map((r) => {
                  const isRegular = r.test?._id;
                  const isPractice = r.testType === 'practice';
                  const to = isRegular
                    ? { pathname: `/tests/${r.test._id}/results`, state: { submissionId: r._id } }
                    : isPractice
                      ? { pathname: `/practice-tests/${r._id}/results` }
                      : null;
                  return (
                    <li key={r._id} className="ci-recent-item">
                      {to ? (
                        <Link to={to} className="ci-recent-link">
                          <span className="ci-recent-name">{r.test?.title || `${r.examType || ''} ${r.subject || ''} Practice`.trim() || 'Test'}</span>
                          <span className="ci-recent-date">{formatDate(r.session?.completedAt || r.createdAt)}</span>
                          <Badge variant={scoreVariant(r.score?.percentage || 0)}>{r.score?.percentage ?? 0}%</Badge>
                        </Link>
                      ) : (
                        <div className="ci-recent-link">
                          <span className="ci-recent-name">{r.test?.title || 'Test'}</span>
                          <span className="ci-recent-date">{formatDate(r.session?.completedAt || r.createdAt)}</span>
                          <Badge variant={scoreVariant(r.score?.percentage || 0)}>{r.score?.percentage ?? 0}%</Badge>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
            <section className="ci-card">
              <h2 className="ci-card-title">Score distribution</h2>
              <div className="ci-distribution">
                {distributionBands.map((band) => {
                  const count = results.filter((r) => {
                    const p = r.score?.percentage ?? 0;
                    return p >= band.min && p < band.max;
                  }).length;
                  const pct = results.length ? (count / results.length) * 100 : 0;
                  return (
                    <div key={band.range} className="ci-dist-row">
                      <span className="ci-dist-range">{band.range}</span>
                      <div className="ci-dist-bar-wrap">
                        <div className={`ci-dist-bar ${band.color}`} style={{ width: `${pct}%` }} />
                        <span className="ci-dist-count">{count}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        )}

        {activeTab === 'subjects' && (
          <div className="ci-subjects">
            {subjectBreakdown.map((s, i) => (
              <section key={i} className="ci-card ci-subject-card">
                <div className="ci-subject-head">
                  <h2 className="ci-card-title">{s.subject}</h2>
                  <Badge variant={scoreVariant(s.averageScore)}>{s.averageScore}%</Badge>
                </div>
                <div className="ci-subject-meta">
                  <span>{s.totalTests} tests</span>
                  <span>{s.passRate}% pass rate</span>
                  <span>{s.passedTests}/{s.totalTests} passed</span>
                </div>
              </section>
            ))}
          </div>
        )}

        {activeTab === 'trends' && (
          <div className="ci-trends">
            <section className="ci-card">
              <h2 className="ci-card-title">5-test moving average</h2>
              {performanceTrends.length > 0 ? (
                <div className="ci-trend-chart">
                  {performanceTrends.map((t, i) => (
                    <div key={i} className="ci-trend-bar-wrap">
                      <div
                        className={`ci-trend-bar ${t.averageScore >= 80 ? 'high' : t.averageScore >= 60 ? 'mid' : 'low'}`}
                        style={{ height: `${t.averageScore}%` }}
                      />
                      <span className="ci-trend-pct">{t.averageScore}%</span>
                      <span className="ci-trend-date">{formatDate(t.date)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="ci-muted">Need at least 5 tests to show trends.</p>
              )}
            </section>
          </div>
        )}

        {activeTab === 'insights' && (
          <div className="ci-insights-tab">
            {generatingInsights && (
              <div className="ci-generating">
                <LoadingSpinner />
                <p>Generating insights…</p>
              </div>
            )}
            {!generatingInsights && comprehensiveInsights && (
              <AIInsights insights={comprehensiveInsights} />
            )}
            {!generatingInsights && !comprehensiveInsights && (
              <div className="ci-no-insights">
                <FiZap size={32} />
                <h3>No insights yet</h3>
                <p>Generate AI insights from all your results above.</p>
                <Button variant="primary" size="small" icon={<FiZap />} onClick={generateComprehensiveInsights}>
                  Generate insights
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default ComprehensiveInsights;